import { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "wouter";
import {
  api, getSession, setSession, login, formatTime, formatDate, formatZar, type Slot, type Club,
} from "../lib/api";
import { Shell } from "../components/Shell";

type Mode = "guest" | "signup" | "signin";

export default function Book() {
  const { slotId } = useParams<{ slotId: string }>();
  const [, navigate] = useLocation();
  const session = getSession();

  const [slot, setSlot] = useState<Slot | null>(null);
  const [club, setClub] = useState<Club | null>(null);
  const [players, setPlayers] = useState(1);
  const [guestNames, setGuestNames] = useState<string[]>([]);
  const [notes, setNotes] = useState("");

  const [mode, setMode] = useState<Mode>(session ? "signin" : "guest");
  const [email, setEmail] = useState(session?.user?.email ?? "");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  // Load slot info via the club's slot listing (cheapest way: try each club we have? we don't know the club).
  // Better: hit a small lookup. We don't have one — fall back to listing slots for today across the test club.
  // Since this MVP has only one bookable club, we can also fetch it directly.
  useEffect(() => {
    (async () => {
      try {
        // Brute force: get bookable clubs, then for each fetch a week of slots and find the slot.
        const clubs = await api.listClubs({ bookable: true });
        for (const c of clubs) {
          for (let i = 0; i < 14; i++) {
            const d = new Date();
            d.setUTCDate(d.getUTCDate() + i);
            const ymd = d.toISOString().slice(0, 10);
            const r = await api.listSlots(c.slug, ymd);
            const found = r.slots.find(s => s.id === slotId);
            if (found) { setSlot(found); setClub(r.club); return; }
          }
        }
        setLoadErr("Tee time not found — it may have just been taken.");
      } catch (e: any) { setLoadErr(e.message); }
    })();
  }, [slotId]);

  const total = slot ? slot.green_fee_cents * players : 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!slot) return;
    setSubmitting(true); setErr(null);
    try {
      const body: any = {
        slot_id: slot.id,
        players,
        guest_names: guestNames.slice(0, players - 1).filter(n => n.trim()),
        notes: notes.trim() || null,
      };
      if (!session && mode !== "signin") {
        body.email = email.trim();
        body.password = password;
        body.display_name = displayName.trim() || null;
        body.phone = phone.trim() || null;
      } else if (!session && mode === "signin") {
        // Sign in first to obtain JWT, then book.
        await login(email.trim(), password);
      }
      const { booking, session: newSess } = await api.createBooking(body);
      if (newSess?.access_token) setSession(newSess);
      navigate(`/me/bookings?new=${booking.id}`);
    } catch (e: any) {
      setErr(e.message ?? "Could not create booking");
    } finally {
      setSubmitting(false);
    }
  }

  if (loadErr) {
    return (
      <Shell>
        <div className="mx-auto max-w-2xl px-6 pt-20">
          <div className="rounded-xl border border-red-900/50 bg-red-950/30 p-6 text-red-200">{loadErr}</div>
          <Link href="/clubs" className="mt-6 inline-block text-accent-400 hover:text-accent-300">← Pick another tee time</Link>
        </div>
      </Shell>
    );
  }
  if (!slot || !club) {
    return (
      <Shell>
        <div className="mx-auto max-w-2xl px-6 pt-20">
          <div className="h-40 rounded-xl bg-neutral-900/50 border border-neutral-900 animate-pulse" />
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mx-auto max-w-3xl px-6 py-16">
        <Link href={`/clubs/${club.slug}`} className="text-sm text-neutral-400 hover:text-neutral-0">← Back to {club.name}</Link>
        <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">Confirm your booking</h1>

        <div className="mt-8 rounded-2xl border border-neutral-800 bg-neutral-900/30 p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500">Tee time</div>
              <div className="mt-1 text-2xl font-medium tabular-nums">{formatTime(slot.start_time)}</div>
              <div className="text-sm text-neutral-400 mt-1">{formatDate(slot.start_time)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-neutral-500">Green fee</div>
              <div className="mt-1 text-2xl font-medium tabular-nums">{formatZar(slot.green_fee_cents)}</div>
              <div className="text-sm text-neutral-400 mt-1">per player</div>
            </div>
          </div>
          <div className="mt-4 text-sm text-neutral-500">{club.name} · Tee {slot.tee}</div>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-6">
          {/* Players */}
          <div>
            <label className="text-sm text-neutral-400">Players</label>
            <div className="mt-2 flex gap-2">
              {[1, 2, 3, 4].map(n => {
                const disabled = n > (slot.seats_remaining ?? slot.max_players);
                return (
                  <button
                    key={n}
                    type="button"
                    disabled={disabled}
                    onClick={() => { setPlayers(n); setGuestNames(arr => arr.slice(0, n - 1)); }}
                    className={
                      "flex-1 rounded-xl px-4 py-3 text-sm font-medium border transition-colors " +
                      (disabled ? "opacity-30 cursor-not-allowed border-neutral-900 bg-neutral-900/30" :
                        n === players
                          ? "bg-accent-500 text-neutral-950 border-accent-500"
                          : "bg-neutral-900/50 border-neutral-800 text-neutral-300 hover:border-neutral-700")
                    }
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Guest names */}
          {players > 1 && (
            <div>
              <label className="text-sm text-neutral-400">Other players (optional)</label>
              <div className="mt-2 space-y-2">
                {Array.from({ length: players - 1 }).map((_, i) => (
                  <input
                    key={i}
                    type="text"
                    placeholder={`Player ${i + 2}`}
                    value={guestNames[i] ?? ""}
                    onChange={e => setGuestNames(arr => { const c = [...arr]; c[i] = e.target.value; return c; })}
                    className="w-full rounded-xl bg-neutral-900/50 border border-neutral-800 px-4 py-3 text-sm focus:outline-none focus:border-accent-500"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-sm text-neutral-400">Notes for the club (optional)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Cart hire, halfway house, special requests…"
              className="mt-2 w-full rounded-xl bg-neutral-900/50 border border-neutral-800 px-4 py-3 text-sm focus:outline-none focus:border-accent-500"
            />
          </div>

          {/* Auth */}
          {!session && (
            <div className="rounded-2xl border border-neutral-800 bg-neutral-900/30 p-5">
              <div className="flex items-center gap-1 text-xs">
                {(["guest", "signup", "signin"] as Mode[]).map(m => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMode(m)}
                    className={"rounded-full px-3 py-1.5 transition-colors " +
                      (mode === m ? "bg-neutral-100 text-neutral-950" : "text-neutral-400 hover:text-neutral-0")}
                  >
                    {m === "guest" ? "Book as guest" : m === "signup" ? "Create account" : "Sign in"}
                  </button>
                ))}
              </div>
              <div className="mt-4 space-y-3">
                <input
                  type="email" required placeholder="you@example.com"
                  value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full rounded-xl bg-neutral-900/50 border border-neutral-800 px-4 py-3 text-sm focus:outline-none focus:border-accent-500"
                />
                <input
                  type="password" required placeholder={mode === "signin" ? "Your password" : "Pick a password (8+ chars)"}
                  minLength={mode === "signin" ? undefined : 8}
                  value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full rounded-xl bg-neutral-900/50 border border-neutral-800 px-4 py-3 text-sm focus:outline-none focus:border-accent-500"
                />
                {mode !== "signin" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      type="text" placeholder="Your name"
                      value={displayName} onChange={e => setDisplayName(e.target.value)}
                      className="rounded-xl bg-neutral-900/50 border border-neutral-800 px-4 py-3 text-sm focus:outline-none focus:border-accent-500"
                    />
                    <input
                      type="tel" placeholder="Phone (optional)"
                      value={phone} onChange={e => setPhone(e.target.value)}
                      className="rounded-xl bg-neutral-900/50 border border-neutral-800 px-4 py-3 text-sm focus:outline-none focus:border-accent-500"
                    />
                  </div>
                )}
                {mode === "guest" && (
                  <p className="text-xs text-neutral-500">
                    We create an account so you can view and cancel this booking. Skip the account fields if you prefer.
                  </p>
                )}
              </div>
            </div>
          )}

          {err && (
            <div className="rounded-xl border border-red-900/50 bg-red-950/30 text-red-200 p-4 text-sm">{err}</div>
          )}

          {/* Total + submit */}
          <div className="flex items-center justify-between border-t border-neutral-900 pt-6">
            <div>
              <div className="text-xs uppercase tracking-wider text-neutral-500">Total · pay at club</div>
              <div className="text-2xl font-semibold tabular-nums mt-1">{formatZar(total)}</div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center rounded-full bg-accent-500 text-neutral-950 px-6 py-3 text-sm font-medium hover:bg-accent-400 transition-colors disabled:opacity-50"
            >
              {submitting ? "Booking…" : "Confirm booking"}
            </button>
          </div>
        </form>
      </div>
    </Shell>
  );
}
